import { api } from './api';

export interface OrgProfile {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  timezone: string;
  status: 'ACTIVE' | 'SUSPENDED';
  createdAt: string;
  updatedAt: string;
}

export interface MemberRow {
  userId: string;
  name: string;
  email: string;
  roleId: string;
  roleName: string;
  status: 'ACTIVE' | 'SUSPENDED';
  joinedAt: string;
  lastLoginAt: string | null;
}

export interface RoleRow {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  templateKey: string | null;
  memberCount: number;
  permissions: string[];
}

export interface OrgInvitationRow {
  id: string;
  email: string;
  roleId: string;
  roleName: string;
  status: 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED';
  invitedByName: string;
  expiresAt: string;
  createdAt: string;
  attemptCount: number;
}

export async function getOrgProfile(orgId: string) {
  const { data } = await api.get<OrgProfile>(`/orgs/${orgId}`);
  return data;
}

export async function updateOrgProfile(
  orgId: string,
  body: Partial<Pick<OrgProfile, 'name' | 'logoUrl' | 'timezone'>>,
) {
  const { data } = await api.patch<OrgProfile>(`/orgs/${orgId}`, body);
  return data;
}

export async function listMembers(
  orgId: string,
  query: {
    search?: string;
    roleId?: string;
    status?: 'ACTIVE' | 'SUSPENDED';
    limit?: number;
    offset?: number;
  } = {},
) {
  const { data } = await api.get<{
    items: MemberRow[];
    total: number;
    limit: number;
    offset: number;
  }>(`/orgs/${orgId}/members`, { params: query });
  return data;
}

export async function listRoles(orgId: string) {
  const { data } = await api.get<{ items: RoleRow[] }>(`/orgs/${orgId}/roles`);
  return data.items;
}

export async function updateMember(
  orgId: string,
  userId: string,
  body: { roleId?: string; status?: 'ACTIVE' | 'SUSPENDED' },
) {
  const { data } = await api.patch<MemberRow>(`/orgs/${orgId}/members/${userId}`, body);
  return data;
}

export async function removeMember(orgId: string, userId: string) {
  const { data } = await api.delete<{ reassignedIssueCount: number }>(
    `/orgs/${orgId}/members/${userId}`,
  );
  return data;
}

export async function listOrgInvitations(
  orgId: string,
  query: {
    status?: 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED';
    limit?: number;
    offset?: number;
  } = {},
) {
  const { data } = await api.get<{
    items: OrgInvitationRow[];
    total: number;
    limit: number;
    offset: number;
  }>(`/orgs/${orgId}/invitations`, { params: query });
  return data;
}

export async function createOrgInvitation(orgId: string, body: { email: string; roleId: string }) {
  const { data } = await api.post<{ invitationId: string }>(`/orgs/${orgId}/invitations`, body);
  return data;
}

export async function resendOrgInvitation(orgId: string, invitationId: string) {
  await api.post(`/orgs/${orgId}/invitations/${invitationId}/resend`);
}

export async function revokeOrgInvitation(orgId: string, invitationId: string) {
  await api.delete(`/orgs/${orgId}/invitations/${invitationId}`);
}
